/* eslint-env browser */
import type { Socket } from 'socket.io-client';
import type { RootState } from '../app/store';
import SOCKET from '../constants/socketTypes';
import { isJudgeDeviceType } from '../constants/deviceType';
import { selectJudge } from '../redux/workSlice';
import { getBundleId } from './buildId';
import packageJson from '../../package.json';

/**
 * How often a device reports itself. It is also the longest marX can stay
 * wrong about which judge a phone holds: each report re-attaches a judge the
 * desktop has lost.
 */
export const HEARTBEAT_INTERVAL_MS = 20000;

// The Battery Status API: Chromium-based browsers in a secure context (the
// https copy of the app). Safari and Firefox have no getBattery at all.
interface BatteryManagerLike extends EventTarget {
  level: number;
  charging: boolean;
}
type NavigatorWithBattery = Navigator & {
  getBattery?: () => Promise<BatteryManagerLike>;
};

/**
 * Report this device to marX - who it is, the judge signed in on it, its
 * battery and the build it runs - on connect, every HEARTBEAT_INTERVAL_MS,
 * when the app comes back to the foreground and when the battery changes. The
 * Devices screen shows it, and marX puts back a judge it has lost track of
 * while the phone still holds them (main.js handleDeviceStatus).
 *
 * Reads the store at send time, never a closure: the socket outlives every
 * judge change (see SocketContext). Returns the function that stops it.
 */
export function startDeviceHeartbeat(
  socket: Socket,
  getState: () => RootState
): () => void {
  let stopped = false;
  let battery: BatteryManagerLike | null = null;

  const send = () => {
    if (stopped || !socket.connected) return;
    try {
      const state = getState();
      const { uuid, deviceId, deviceType } = state.settings;
      const judge = selectJudge(state);
      // Only a judge's phone speaks for a judge: a stale judge left in the
      // store of a phone now used as MC, monitor or for training must not.
      const claimsJudge =
        isJudgeDeviceType(deviceType) && Boolean(judge.uuid && judge.letter);
      socket.emit(SOCKET.DEVICE_STATUS, {
        deviceUuid: uuid,
        deviceId,
        deviceType,
        adjudicator: claimsJudge
          ? { adjudicatorUuid: judge.uuid, letter: judge.letter, name: judge.name }
          : null,
        battery: battery
          ? { level: battery.level, charging: battery.charging }
          : null,
        version: packageJson.version,
        bundleId: getBundleId()
      });
    } catch (err) {
      console.error('Error sending device status:', (err as Error).message);
    }
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') send();
  };

  const interval = setInterval(send, HEARTBEAT_INTERVAL_MS);
  socket.on('connect', send);
  document.addEventListener('visibilitychange', onVisibilityChange);

  (navigator as NavigatorWithBattery)
    .getBattery?.()
    .then((manager) => {
      if (stopped) return;
      battery = manager;
      manager.addEventListener('levelchange', send);
      manager.addEventListener('chargingchange', send);
      send();
    })
    .catch(() => {
      // Not available (insecure context, blocked by policy): reported as null.
    });

  send();

  return () => {
    stopped = true;
    clearInterval(interval);
    socket.off('connect', send);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    if (battery) {
      battery.removeEventListener('levelchange', send);
      battery.removeEventListener('chargingchange', send);
    }
  };
}
